import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import VerificationModal from '../Common/VerificationModal';
import './JoinWaitlistPage.css'; // We'll create this for specific styles

// Cognito configuration for waitlist (same as in LoggedOutHome)
// Consider moving this to a central Amplify configuration if not already done
const waitlistConfig = {
  Auth: {
    Cognito: {
      region: 'us-east-1',
      userPoolId: 'us-east-1_oqESegkRi',
      userPoolClientId: '27rlvno93hn533psvrhabfh3cm',
    }
  }
};
Amplify.configure(waitlistConfig);

function JoinWaitlistPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    userType: {},
    interestReason: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleUserTypeChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      userType: {
        ...prevState.userType,
        [name]: checked,
      },
    }));
  };

  const generateTempPassword = () => {
    return Math.random().toString(36).slice(-8) + 'A1!'; // Simple temporary password
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    const userTypeMapping = {
      myself: 'For myself',
      child: 'For a child',
      parent: 'For an elderly parent',
      other: 'Other',
    };

    const selectedUserTypes = Object.entries(formData.userType)
      .filter(([, checked]) => checked)
      .map(([key]) => userTypeMapping[key])
      .join(', ');

    try {
      await signUp({
        username: formData.email,
        password: generateTempPassword(),
        options: {
          userAttributes: {
            email: formData.email,
            'custom:full_name': formData.fullName,
            'custom:user_type': selectedUserTypes,
            'custom:interest_reason': formData.interestReason,
          },
        }
      });
      setShowVerificationModal(true);
      setSubmitError(null);
    } catch (error) {
      setSubmitError(error.message);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setVerificationError('');
    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode: verificationCode
      });
      setShowVerificationModal(false);
      setSubmitSuccess(true);
      setFormData({ fullName: '', email: '', userType: {}, interestReason: '' }); // Clear form on success
    } catch (error) {
      setVerificationError(error.message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Container className="join-waitlist-container py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="p-4 shadow-sm">
            <Card.Body>
              <h1 className="text-center mb-4">Join Our Waitlist</h1>
              <p className="text-center text-muted mb-4">
                Be among the first to experience Asha AI. Sign up to get an invitation. No credit card required.
              </p>
              {submitSuccess ? (
                <Alert variant="success">
                  Thank you for joining! Please check your email to verify your address and complete the process.
                  Once verified, we'll keep you updated on our progress.
                </Alert>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4" controlId="waitlistFullName">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      size="lg"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="waitlistEmail">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      size="lg"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="waitlistUserType">
                    <Form.Label>How do you plan to use Asha AI?</Form.Label>
                    <div className="user-type-checkbox-group">
                      <Form.Check 
                        type="checkbox"
                        id="userType-myself"
                        label="For myself"
                        name="myself"
                        checked={!!formData.userType.myself}
                        onChange={handleUserTypeChange}
                        className="mb-2"
                      />
                      <Form.Check 
                        type="checkbox"
                        id="userType-child"
                        label="For a child"
                        name="child"
                        checked={!!formData.userType.child}
                        onChange={handleUserTypeChange}
                        className="mb-2"
                      />
                      <Form.Check 
                        type="checkbox"
                        id="userType-parent"
                        label="For an elderly parent"
                        name="parent"
                        checked={!!formData.userType.parent}
                        onChange={handleUserTypeChange}
                        className="mb-2"
                      />
                      <Form.Check 
                        type="checkbox"
                        id="userType-other"
                        label="Other"
                        name="other"
                        checked={!!formData.userType.other}
                        onChange={handleUserTypeChange}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="waitlistInterestReason">
                    <Form.Label>What features are you most interested in?</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="e.g., AI chat, health tracking, document analysis"
                      name="interestReason"
                      value={formData.interestReason}
                      onChange={handleInputChange}
                      size="lg"
                    />
                  </Form.Group>

                  {submitError && (
                    <Alert variant="danger" className="mb-3">
                      {submitError}
                    </Alert>
                  )}

                  <Button
                    variant="info"
                    size="lg"
                    type="submit"
                    className="w-100 main-cta-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request an Invite'}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <VerificationModal
        show={showVerificationModal}
        onHide={() => setShowVerificationModal(false)}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        onSubmit={handleVerification}
        verificationError={verificationError}
        verifying={verifying}
      />
    </Container>
  );
}

export default JoinWaitlistPage; 